/**
 * トラフィックモニター（章 6）。
 *
 * リンクごとの使用率と、通信量の多い機器の順位を見せる、測定用の道具。
 * 電波スキャナー（ui/scanner.ts）と同じ作りにそろえてある。
 * **設定を変える機能は持たない。** 隔離もポートの停止も、機器の管理画面の仕事。
 *
 * 【厳守】
 * - 表示する値はすべて sim/traffic.ts の計算結果。数値をここに書かない
 * - **犯人の名前をここに書かない。** 異常な 1 台は detectAnomaly() が
 *   通信量の偏りだけから割り出す。世界の infected フラグは覗かない
 *   （プレイヤーが計器から読み取れない情報で答えを出さない）
 *
 * 【画面の並びの理由】
 * 章 6 の学びは「**全員が遅いときに全員を疑わない。共通して通る場所を見る**」。
 * だから上から順に
 *   1. 異常な 1 台（あれば）— ここが最優先
 *   2. いちばん混んでいるリンク
 *   3. リンク別の使用率（混んでいる順）
 *   4. 上位通信元
 * と、「どこが詰まっているか」→「誰のせいか」の順で読めるようにする。
 */

import type { LinkId, NetworkWorld } from '../types/network';
import {
  busiestLink,
  detectAnomaly,
  linkUtilization,
  topTalkers,
  utilizationTone,
  UTILIZATION_SATURATED_PERCENT,
  UTILIZATION_WARN_PERCENT,
  type LinkLoad,
  type LinkUtilizationMap,
  type TopTalker,
  type TrafficAnomaly,
} from '../sim/traffic';
import { button, clear, el } from './dom';

/** 上位通信元に並べる最大件数 */
export const TALKER_LIMIT = 8;

export interface TrafficMonitorHooks {
  onClose: () => void;
}

export class TrafficMonitor {
  readonly root: HTMLElement;

  private readonly alertNode: HTMLElement;
  private readonly summaryNode: HTMLElement;
  private readonly linksNode: HTMLElement;
  private readonly talkersNode: HTMLElement;

  private world: NetworkWorld | null = null;

  constructor(private readonly hooks: TrafficMonitorHooks) {
    this.alertNode = el('div', { class: 'mon-alert-slot' });
    this.summaryNode = el('div', { class: 'mon-summary' });
    this.linksNode = el('div', { class: 'mon-links' });
    this.talkersNode = el('div', { class: 'mon-talkers' });

    this.root = el('div', { class: 'mon-layer', hidden: true }, [
      el('div', { class: 'mon-window' }, [
        el('div', { class: 'mon-head' }, [
          el('span', { class: 'mon-title', text: 'トラフィックモニター' }),
          button('閉じる (M / Esc)', () => this.hooks.onClose(), 'btn btn-ghost btn-small'),
        ]),
        this.alertNode,
        this.summaryNode,
        this.linksNode,
        this.talkersNode,
        el('div', { class: 'mon-legend' }, [
          legendItem('ok', `正常（${UTILIZATION_WARN_PERCENT}% 未満）`),
          legendItem('warn', `混雑（${UTILIZATION_WARN_PERCENT}% 以上）`),
          legendItem('saturated', `飽和（${UTILIZATION_SATURATED_PERCENT}% 以上）`),
        ]),
      ]),
    ]);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(world: NetworkWorld): void {
    this.world = world;
    this.root.hidden = false;
    this.render();
  }

  close(): void {
    this.root.hidden = true;
  }

  render(): void {
    clear(this.alertNode);
    clear(this.summaryNode);
    clear(this.linksNode);
    clear(this.talkersNode);

    const world = this.world;
    if (!world) return;

    const load = linkUtilization(world);
    const talkers = topTalkers(world);
    const anomaly = detectAnomaly(world, talkers);

    this.renderAlert(anomaly);
    this.renderSummary(world, load, talkers);
    this.renderLinks(world, load);
    this.renderTalkers(talkers, anomaly);
  }

  /**
   * 異常な 1 台。**章 6 の学習の肝なので、いちばん上にいちばん大きく出す。**
   * 判定は sim 側（detectAnomaly）に置いてあり、ここは表示だけを受け持つ。
   */
  private renderAlert(anomaly: TrafficAnomaly | null): void {
    if (!anomaly) return;
    const share = Math.round(anomaly.share * 100);
    const ratio = Number.isFinite(anomaly.ratioToNext)
      ? `2位の約 ${anomaly.ratioToNext} 倍`
      : '他に通信している機器なし';
    this.alertNode.append(
      el('div', { class: 'mon-alert' }, [
        el('span', { class: 'mon-alert-mark', text: '!' }),
        el('div', { class: 'mon-alert-body' }, [
          el('div', { class: 'mon-alert-head', text: '突出している通信元があります' }),
          el('div', { class: 'mon-alert-name', text: anomaly.label }),
          el('div', {
            class: 'mon-alert-note',
            text: `${anomaly.mbps} Mbps — 全体の ${share}%、${ratio}。この 1 台だけが桁違いです。`,
          }),
        ]),
      ]),
    );
  }

  private renderSummary(
    world: NetworkWorld,
    load: LinkUtilizationMap,
    talkers: TopTalker[],
  ): void {
    const linkIds = Object.keys(load);
    const busiest = busiestLink(world, load);
    const saturated = linkIds.filter(
      (id) => load[id].percent >= UTILIZATION_SATURATED_PERCENT,
    ).length;
    const total = Math.round(talkers.reduce((sum, talker) => sum + talker.mbps, 0) * 10) / 10;

    this.summaryNode.append(
      summaryItem('監視中のリンク', `${linkIds.length} 本`),
      summaryItem(
        'いちばん混んでいるリンク',
        busiest ? linkName(world, busiest.linkId) : '—',
        busiest ? utilizationTone(busiest.load.percent) : undefined,
      ),
      summaryItem(
        'その使用率',
        busiest ? `${busiest.load.percent} %` : '—',
        busiest ? utilizationTone(busiest.load.percent) : undefined,
      ),
      summaryItem('飽和しているリンク', `${saturated} 本`, saturated > 0 ? 'saturated' : 'ok'),
      summaryItem('社内から出ている通信量', `${total} Mbps`),
    );
  }

  /** リンク別の使用率。混んでいる順に並べる（詰まっている場所が先頭に来る） */
  private renderLinks(world: NetworkWorld, load: LinkUtilizationMap): void {
    this.linksNode.append(
      el('div', { class: 'mon-section-title', text: 'リンクごとの使用率' }),
    );
    const linkIds = Object.keys(load).sort(
      (a, b) => load[b].percent - load[a].percent || a.localeCompare(b),
    );
    if (linkIds.length === 0) {
      this.linksNode.append(el('p', { class: 'mon-empty', text: '監視できるリンクがありません。' }));
      return;
    }
    for (const linkId of linkIds) {
      this.linksNode.append(linkRow(linkName(world, linkId), load[linkId]));
    }
  }

  /** 上位通信元。異常と判定された 1 台には印を付ける */
  private renderTalkers(talkers: TopTalker[], anomaly: TrafficAnomaly | null): void {
    this.talkersNode.append(el('div', { class: 'mon-section-title', text: '上位通信元' }));
    if (talkers.length === 0) {
      this.talkersNode.append(
        el('p', { class: 'mon-empty', text: 'いま通信を出している機器はありません。' }),
      );
      return;
    }
    const max = talkers[0].mbps || 1;
    talkers.slice(0, TALKER_LIMIT).forEach((talker, index) => {
      const isAnomaly = anomaly?.deviceId === talker.deviceId;
      const bar = el('div', { class: 'mon-bar' });
      const fill = el('div', {
        class: isAnomaly ? 'mon-fill mon-fill-saturated' : 'mon-fill mon-fill-ok',
      });
      fill.style.width = `${Math.max(2, Math.min(100, (talker.mbps / max) * 100))}%`;
      bar.append(fill);

      this.talkersNode.append(
        el('div', { class: isAnomaly ? 'mon-talker anomaly' : 'mon-talker' }, [
          el('span', { class: 'mon-rank', text: `${index + 1}` }),
          el('span', { class: 'mon-talker-name', text: talker.label }),
          bar,
          el('span', { class: 'mon-talker-value', text: `${talker.mbps} Mbps` }),
        ]),
      );
    });
  }
}

/** 「機器A ⇄ 機器B」。ラベルが引けなければ機器 ID を出す */
function linkName(world: NetworkWorld, linkId: LinkId): string {
  const link = world.links[linkId];
  if (!link) return linkId;
  const label = (deviceId: string): string => world.devices[deviceId]?.label ?? deviceId;
  return `${label(link.a.deviceId)} ⇄ ${label(link.b.deviceId)}`;
}

function linkRow(name: string, load: LinkLoad): HTMLElement {
  const tone = utilizationTone(load.percent);
  const bar = el('div', { class: 'mon-bar' });
  const fill = el('div', { class: `mon-fill mon-fill-${tone}` });
  // 100% を超えても棒は振り切れたまま。超過分は数字で読ませる
  fill.style.width = `${Math.min(100, Math.max(0, load.percent))}%`;
  bar.append(fill);

  return el('div', { class: `mon-row mon-row-${tone}` }, [
    el('span', { class: 'mon-link-name', text: name }),
    bar,
    el('span', { class: `mon-percent ${tone}`, text: `${load.percent} %` }),
    el('span', { class: 'mon-detail', text: `${load.mbps} / ${load.capacityMbps} Mbps` }),
  ]);
}

function summaryItem(label: string, value: string, tone?: string): HTMLElement {
  return el('div', { class: 'mon-item' }, [
    el('span', { class: 'mon-item-label', text: label }),
    el('span', { class: tone ? `mon-item-value ${tone}` : 'mon-item-value', text: value }),
  ]);
}

function legendItem(tone: string, label: string): HTMLElement {
  return el('span', { class: 'mon-legend-item' }, [
    el('span', { class: `mon-swatch mon-fill-${tone}` }),
    el('span', { text: label }),
  ]);
}
